import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import LookupSelect from '../components/ui/LookupSelect';
import ContactSelect from '../components/ui/ContactSelect';
import { formatDate, formatSerial, todayLocal } from '../lib/utils';
import AcquisitionTypeToggle from '../components/ui/AcquisitionTypeToggle';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiDownload, FiEye, FiX, FiToggleLeft, FiToggleRight, FiMessageSquare, FiSend, FiCalendar, FiLoader, FiEdit2, FiSave } from 'react-icons/fi';

const acqTypeLabel = (t) => (t === 'REPAIR' ? 'Repair' : 'New');

export default function BoundBook() {
  const { tenant } = useAuth();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDisposed, setShowDisposed] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [notesRecord, setNotesRecord] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchBook = async () => {
    if (!tenant) return;
    try {
      setLoading(true);
      const res = await api.getBoundBook(tenant.id);
      setData(res);
    } catch (err) {
      console.error("Fetch book error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBook();
  }, [tenant]);

  const filteredData = data.filter(item => {
    if (!showDisposed && item.status === 'DISPOSED') return false;

    // Date range filter on acquisition_date
    if (dateFrom && item.acquisition_date < dateFrom) return false;
    if (dateTo && item.acquisition_date > dateTo) return false;

    const term = searchTerm.toLowerCase();
    return (
      (item.serial_number?.toLowerCase() || '').includes(term) ||
      (item.manufacturer?.toLowerCase() || '').includes(term) ||
      (item.importer?.toLowerCase() || '').includes(term) ||
      (item.model?.toLowerCase() || '').includes(term)
    );
  });

  const handleExportCSV = async () => {
    if (filteredData.length === 0) return;
    setExporting(true);
    try {
      // Fetch notes for all visible records in parallel
      const notesMap = {};
      const notePromises = filteredData.map(async (item) => {
        try {
          const notes = await api.getEntryNotes(item.id);
          notesMap[item.id] = notes;
        } catch {
          notesMap[item.id] = [];
        }
      });
      await Promise.all(notePromises);

      // Build CSV
      const headers = [
        'Status',
        'Serial Number',
        'Manufacturer',
        'Importer',
        'Model',
        'Caliber',
        'Type',
        'Acquisition Date',
        'Acquisition Type',
        'Acquired From - Business',
        'Acquired From - FFL',
        'Acquired From - Contact',
        'Acquired From - Phone',
        'Acquired From - Email',
        'Acquired From - Address',
        'Disposition Date',
        'Disposed To - Business',
        'Disposed To - FFL',
        'Disposed To - Contact',
        'Form 4473 Reference',
        'Notes'
      ];

      const escapeCSV = (val) => {
        const str = val == null ? '' : String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };

      const rows = filteredData.map(item => {
        const acq = item.acquired_from_detail;
        const disp = item.disposed_to_detail;
        const entryNotes = notesMap[item.id] || [];
        const notesText = entryNotes
          .map(n => `[${new Date(n.created_at).toLocaleString()}] ${n.created_by_name}: ${n.note}`)
          .join(' | ');

        return [
          item.status,
          formatSerial(item.serial_number, item.is_non_serialized),
          item.manufacturer,
          item.importer,
          item.model,
          item.caliber,
          item.type,
          item.acquisition_date,
          acqTypeLabel(item.acquisition_type),
          acq?.name,
          acq?.ffl_number,
          acq?.contact_name,
          acq?.phone,
          acq?.email,
          acq?.address,
          item.disposition_date,
          disp?.name,
          disp?.ffl_number,
          disp?.contact_name,
          item.form_4473_reference,
          notesText
        ].map(escapeCSV).join(',');
      });

      const csv = [headers.map(escapeCSV).join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = todayLocal();
      link.download = `bound-book-export-${dateStr}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert('Error exporting CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const clearDateFilters = () => {
    setDateFrom('');
    setDateTo('');
  };

  const hasDateFilter = dateFrom || dateTo;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            Electronic Bound Book
          </h2>
          <p className="text-sm text-slate-500">Official ATF compliant record of acquisitions and dispositions.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
            <Input
              placeholder="Search serial, model..."
              className="pl-9 w-[250px]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            icon={exporting ? FiLoader : FiDownload}
            onClick={handleExportCSV}
            disabled={exporting || filteredData.length === 0}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <button
          onClick={() => setShowDisposed(!showDisposed)}
          className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
        >
          <SafeIcon
            icon={showDisposed ? FiToggleRight : FiToggleLeft}
            className={`h-6 w-6 ${showDisposed ? 'text-primary-600' : 'text-slate-400'}`}
          />
          Show Disposed
        </button>

        <div className="h-5 w-px bg-slate-300 dark:bg-slate-700" />

        <div className="flex items-center gap-2">
          <SafeIcon icon={FiCalendar} className="h-4 w-4 text-slate-500" />
          <span className="text-sm text-slate-600 dark:text-slate-400">From:</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
          />
          <span className="text-sm text-slate-600 dark:text-slate-400">To:</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white"
          />
          {hasDateFilter && (
            <button
              onClick={clearDateFilters}
              className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="w-full overflow-x-scroll custom-scrollbar">
        <Card className="w-fit min-w-full">
          <table className="w-full text-sm text-left whitespace-nowrap">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Serial Number</th>
                <th className="px-6 py-3">Manufacturer / Importer</th>
                <th className="px-6 py-3">Model</th>
                <th className="px-6 py-3">Acquired Date</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {loading ? (
                <tr><td colSpan="6" className="p-8 text-center">Loading compliance records...</td></tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-12 text-center">
                    <div className="flex flex-col items-center justify-center text-slate-500">
                      <SafeIcon icon={FiSearch} className="h-12 w-12 mb-2 opacity-20" />
                      <p>No records found in the bound book.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.id} className="bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start gap-1">
                        <Badge variant={item.status === 'IN_HOUSE' ? 'success' : 'default'}>
                          {item.status.replace('_', ' ')}
                        </Badge>
                        <Badge variant={item.acquisition_type === 'REPAIR' ? 'warning' : 'primary'}>
                          {acqTypeLabel(item.acquisition_type)}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-mono font-medium">{formatSerial(item.serial_number, item.is_non_serialized)}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span>{item.manufacturer}</span>
                        {item.importer && <span className="text-xs text-slate-500">{item.importer}</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4">{item.model}</td>
                    <td className="px-6 py-4">{formatDate(item.acquisition_date)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => setSelectedRecord(item)}
                          className="inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 transition-colors"
                        >
                          <SafeIcon icon={FiEye} className="h-3.5 w-3.5" />
                          View
                        </button>
                        <button
                          onClick={() => setNotesRecord(item)}
                          className="inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
                        >
                          <SafeIcon icon={FiMessageSquare} className="h-3.5 w-3.5" />
                          Notes
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Card>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-400">
        <p>ATF Regulation 27 CFR § 478.125(e) Compliant Record</p>
        <p>Total Records: {filteredData.length}</p>
      </div>

      {selectedRecord && (
        <RecordDetailModal
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
          onSave={() => { fetchBook(); setSelectedRecord(null); }}
        />
      )}

      {notesRecord && (
        <NotesModal record={notesRecord} onClose={() => setNotesRecord(null)} />
      )}
    </div>
  );
}

function NotesModal({ record, onClose }) {
  const { tenant, user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const data = await api.getEntryNotes(record.id);
      setNotes(data);
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [record.id]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleAdd = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const created = await api.addEntryNote(
        tenant.id,
        record.id,
        newNote.trim(),
        user.id,
        user.name || user.email || 'Unknown'
      );
      setNotes([...notes, created]);
      setNewNote('');
    } catch (err) {
      console.error('Error adding note:', err);
      alert(`Error adding note: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg mx-4 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Notes</h3>
            <p className="text-xs text-slate-500">
              {record.manufacturer} {record.model} — SN: {formatSerial(record.serial_number, record.is_non_serialized)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors"
          >
            <SafeIcon icon={FiX} className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {loading ? (
            <p className="text-center text-sm text-slate-500 py-8">Loading notes...</p>
          ) : notes.length === 0 ? (
            <div className="text-center py-8">
              <SafeIcon icon={FiMessageSquare} className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700 mb-2" />
              <p className="text-sm text-slate-500">No notes yet. Add the first note below.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notes.map((note) => (
                <div key={note.id} className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3">
                  <p className="text-sm text-slate-900 dark:text-white whitespace-pre-wrap">{note.note}</p>
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <span className="font-medium">{note.created_by_name}</span>
                    <span>&middot;</span>
                    <span>{formatDate(note.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="shrink-0 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <div className="flex gap-2">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add a note..."
              rows={2}
              className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-800 dark:border-slate-700 dark:text-white"
            />
            <Button
              onClick={handleAdd}
              disabled={!newNote.trim() || saving}
              isLoading={saving}
              icon={FiSend}
              className="self-end"
            >
              Add
            </Button>
          </div>
          <p className="mt-1 text-xs text-slate-400">Press Enter to submit. Notes cannot be deleted.</p>
        </div>
      </div>
    </div>
  );
}

function RecordDetailModal({ record, onClose, onSave }) {
  const { tenant, user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [resolvedNames, setResolvedNames] = useState({});

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const startEditing = () => {
    setEditData({
      serial_number: record.serial_number || '',
      is_non_serialized: record.is_non_serialized || false,
      manufacturer_id: record.manufacturer_id || '',
      importer_id: record.importer_id || '',
      model_id: record.model_id || '',
      type_id: record.type_id || '',
      caliber_id: record.caliber_id || '',
      acquisition_date: record.acquisition_date || '',
      acquisition_type: record.acquisition_type || 'NEW',
      acquired_from_id: record.acquired_from_id || '',
      disposition_date: record.disposition_date || '',
      disposed_to_id: record.disposed_to_id || '',
      form_4473_reference: record.form_4473_reference || '',
    });
    setResolvedNames({
      manufacturer: record.manufacturer || '',
      importer: record.importer || '',
      model: record.model || '',
      type: record.type || '',
      caliber: record.caliber || '',
      acquired_from: record.acquired_from_name || '',
      disposed_to: record.disposed_to_name || '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const changes = {};
      const descriptions = [];

      // Compare firearm fields
      if (editData.is_non_serialized !== (record.is_non_serialized || false)) {
        if (editData.is_non_serialized) {
          changes.is_non_serialized = true;
          changes.serial_number = `NSN-${crypto.randomUUID()}`;
          descriptions.push(`Serial number changed from "${formatSerial(record.serial_number, record.is_non_serialized)}" to "NSN"`);
        } else {
          changes.is_non_serialized = false;
          changes.serial_number = editData.serial_number;
          descriptions.push(`Changed from non-serialized to serial number "${editData.serial_number}"`);
        }
      } else if (!editData.is_non_serialized && editData.serial_number !== record.serial_number) {
        changes.serial_number = editData.serial_number;
        descriptions.push(`Serial number changed from "${record.serial_number}" to "${editData.serial_number}"`);
      }

      if (editData.manufacturer_id !== (record.manufacturer_id || '')) {
        changes.manufacturer_id = editData.manufacturer_id;
        descriptions.push(`Manufacturer changed from "${record.manufacturer || '-'}" to "${resolvedNames.manufacturer || '-'}"`);
      }
      if (editData.importer_id !== (record.importer_id || '')) {
        changes.importer_id = editData.importer_id || null;
        descriptions.push(`Importer changed from "${record.importer || 'None'}" to "${resolvedNames.importer || 'None'}"`);
      }
      if (editData.model_id !== (record.model_id || '')) {
        changes.model_id = editData.model_id;
        descriptions.push(`Model changed from "${record.model || '-'}" to "${resolvedNames.model || '-'}"`);
      }
      if (editData.type_id !== (record.type_id || '')) {
        changes.type_id = editData.type_id;
        descriptions.push(`Firearm type changed from "${record.type || '-'}" to "${resolvedNames.type || '-'}"`);
      }
      if (editData.caliber_id !== (record.caliber_id || '')) {
        changes.caliber_id = editData.caliber_id;
        descriptions.push(`Caliber changed from "${record.caliber || '-'}" to "${resolvedNames.caliber || '-'}"`);
      }
      if (editData.acquisition_date !== (record.acquisition_date || '')) {
        changes.acquisition_date = editData.acquisition_date;
        descriptions.push(`Acquisition date changed from "${formatDate(record.acquisition_date)}" to "${formatDate(editData.acquisition_date)}"`);
      }
      if (editData.acquisition_type !== (record.acquisition_type || 'NEW')) {
        changes.acquisition_type = editData.acquisition_type;
        descriptions.push(`Acquisition type changed from "${acqTypeLabel(record.acquisition_type)}" to "${acqTypeLabel(editData.acquisition_type)}"`);
      }
      if (editData.acquired_from_id !== (record.acquired_from_id || '')) {
        changes.acquired_from_id = editData.acquired_from_id;
        descriptions.push(`Acquired-from contact changed from "${record.acquired_from_name || '-'}" to "${resolvedNames.acquired_from || '-'}"`);
      }
      if (editData.disposition_date !== (record.disposition_date || '')) {
        changes.disposition_date = editData.disposition_date || null;
        descriptions.push(`Disposition date changed from "${formatDate(record.disposition_date) || '-'}" to "${formatDate(editData.disposition_date) || '-'}"`);
      }
      if (editData.disposed_to_id !== (record.disposed_to_id || '')) {
        changes.disposed_to_id = editData.disposed_to_id || null;
        descriptions.push(`Disposed-to contact changed from "${record.disposed_to_name || '-'}" to "${resolvedNames.disposed_to || '-'}"`);
      }
      if (editData.form_4473_reference !== (record.form_4473_reference || '')) {
        changes.form_4473_reference = editData.form_4473_reference || null;
        descriptions.push(`Form 4473 reference changed from "${record.form_4473_reference || '-'}" to "${editData.form_4473_reference || '-'}"`);
      }

      if (descriptions.length === 0) {
        setEditing(false);
        return;
      }

      changes._descriptions = descriptions;
      await api.updateBoundBookEntry(tenant.id, record, changes, user.id, user.name || user.email || 'Unknown');
      onSave();
    } catch (err) {
      console.error('Edit error:', err);
      alert(err.message || 'Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  const acq = record.acquired_from_detail;
  const disp = record.disposed_to_detail;

  // VIEW MODE fields
  const viewFields = [
    { label: 'Status', value: (
      <Badge variant={record.status === 'IN_HOUSE' ? 'success' : 'default'}>
        {record.status.replace('_', ' ')}
      </Badge>
    )},
    { label: 'Serial Number', value: formatSerial(record.serial_number, record.is_non_serialized) },
    { label: 'Manufacturer', value: record.manufacturer },
    { label: 'Importer', value: record.importer || 'N/A (Domestic)' },
    { label: 'Model', value: record.model },
    { label: 'Caliber', value: record.caliber },
    { label: 'Type', value: record.type },
    { label: 'Acquisition Date', value: formatDate(record.acquisition_date) },
    { label: 'Acquisition Type', value: acqTypeLabel(record.acquisition_type) },
    { section: 'Acquired From' },
    { label: acq?.type === 'CUSTOMER' ? 'Name' : 'Business Name', value: acq?.name || '-' },
    ...(acq?.type !== 'CUSTOMER' ? [
      { label: 'FFL Number', value: acq?.ffl_number || '-' },
      { label: 'Contact Name', value: acq?.contact_name || '-' },
    ] : []),
    { label: 'Phone', value: acq?.phone || '-' },
    { label: 'Email', value: acq?.email || '-' },
    { label: 'Address', value: acq?.address || '-' },
    { section: 'Disposition' },
    { label: 'Disposition Date', value: formatDate(record.disposition_date) || '-' },
    { label: disp?.type === 'CUSTOMER' ? 'Name' : 'Business Name', value: disp?.name || '-' },
    ...(disp?.type !== 'CUSTOMER' ? [
      { label: 'FFL Number', value: disp?.ffl_number || '-' },
      { label: 'Contact Name', value: disp?.contact_name || '-' },
    ] : []),
    { label: 'Form 4473 Reference', value: record.form_4473_reference || '-' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg mx-4 bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {editing ? 'Edit Record' : 'Record Details'}
          </h3>
          <div className="flex items-center gap-2">
            {!editing && (
              <button
                onClick={startEditing}
                className="rounded-lg p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-slate-800 transition-colors"
                title="Edit record"
              >
                <SafeIcon icon={FiEdit2} className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors"
            >
              <SafeIcon icon={FiX} className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {editing ? (
            <div className="space-y-4">
              {/* Serial Number */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    id="edit_nsn"
                    checked={editData.is_non_serialized}
                    onChange={(e) => setEditData({
                      ...editData,
                      is_non_serialized: e.target.checked,
                      serial_number: e.target.checked ? '' : editData.serial_number
                    })}
                    className="h-4 w-4 rounded border-slate-300 text-primary-500 focus:ring-primary-500"
                  />
                  <label htmlFor="edit_nsn" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Non-Serialized (NSN)
                  </label>
                </div>
                {editData.is_non_serialized ? (
                  <div className="flex h-10 w-full items-center rounded-md border border-slate-300 bg-slate-100 px-3 text-sm text-slate-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400">
                    NSN — No Serial Number
                  </div>
                ) : (
                  <Input
                    label="Serial Number"
                    value={editData.serial_number}
                    onChange={(e) => setEditData({ ...editData, serial_number: e.target.value })}
                    required
                  />
                )}
              </div>

              {/* Manufacturer */}
              <LookupSelect
                table="manufacturers"
                label="Manufacturer"
                value={editData.manufacturer_id}
                onChange={(val) => setEditData({ ...editData, manufacturer_id: val })}
                onSelectItem={(item) => setResolvedNames(prev => ({ ...prev, manufacturer: item?.name || '' }))}
                required
              />

              {/* Importer */}
              <LookupSelect
                table="importers"
                label="Importer"
                value={editData.importer_id}
                onChange={(val) => setEditData({ ...editData, importer_id: val })}
                onSelectItem={(item) => setResolvedNames(prev => ({ ...prev, importer: item?.name || '' }))}
              />

              {/* Model */}
              <LookupSelect
                table="models"
                label="Model"
                value={editData.model_id}
                onChange={(val) => setEditData({ ...editData, model_id: val })}
                onSelectItem={(item) => setResolvedNames(prev => ({ ...prev, model: item?.name || '' }))}
                filters={editData.manufacturer_id ? { manufacturer_id: editData.manufacturer_id } : {}}
                required
              />

              {/* Type */}
              <LookupSelect
                table="types"
                label="Firearm Type"
                value={editData.type_id}
                onChange={(val) => setEditData({ ...editData, type_id: val })}
                onSelectItem={(item) => setResolvedNames(prev => ({ ...prev, type: item?.name || '' }))}
                required
              />

              {/* Caliber */}
              <LookupSelect
                table="calibers"
                label="Caliber"
                value={editData.caliber_id}
                onChange={(val) => setEditData({ ...editData, caliber_id: val })}
                onSelectItem={(item) => setResolvedNames(prev => ({ ...prev, caliber: item?.name || '' }))}
                required
              />

              {/* Acquisition Date */}
              <Input
                label="Acquisition Date"
                type="date"
                value={editData.acquisition_date}
                onChange={(e) => setEditData({ ...editData, acquisition_date: e.target.value })}
                required
              />

              {/* Acquisition Type */}
              <AcquisitionTypeToggle
                value={editData.acquisition_type}
                onChange={(val) => setEditData({ ...editData, acquisition_type: val })}
              />

              {/* Acquired From */}
              <ContactSelect
                label="Acquired From"
                value={editData.acquired_from_id}
                onChange={(val) => setEditData({ ...editData, acquired_from_id: val })}
                onSelectContact={(c) => setResolvedNames(prev => ({ ...prev, acquired_from: c?.name || '' }))}
                required
              />

              {/* Disposition section */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Disposition</h4>
              </div>

              <Input
                label="Disposition Date"
                type="date"
                value={editData.disposition_date}
                onChange={(e) => setEditData({ ...editData, disposition_date: e.target.value })}
              />

              <ContactSelect
                label="Disposed To"
                value={editData.disposed_to_id}
                onChange={(val) => setEditData({ ...editData, disposed_to_id: val })}
                onSelectContact={(c) => setResolvedNames(prev => ({ ...prev, disposed_to: c?.name || '' }))}
              />

              <Input
                label="Form 4473 Reference"
                value={editData.form_4473_reference}
                onChange={(e) => setEditData({ ...editData, form_4473_reference: e.target.value })}
              />
            </div>
          ) : (
            <dl className="space-y-4">
              {viewFields.map((field, i) =>
                field.section ? (
                  <div key={field.section} className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white">{field.section}</h4>
                  </div>
                ) : (
                  <div key={`${field.label}-${i}`} className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                    <dt className="text-sm font-medium text-slate-500 dark:text-slate-400 sm:w-44 shrink-0">{field.label}</dt>
                    <dd className="text-sm text-slate-900 dark:text-white">{field.value}</dd>
                  </div>
                )
              )}
            </dl>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          {editing ? (
            <>
              <Button variant="secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleSave} isLoading={saving} icon={FiSave}>Save Changes</Button>
            </>
          ) : (
            <Button variant="secondary" onClick={onClose}>Close</Button>
          )}
        </div>
      </div>
    </div>
  );
}
